/**
 * Semantica de las salidas digitales del generador (FSM NANOOX).
 *
 * El controlador publica `digital_outputs_state` como un string de bits, y
 * `control_generator_back` como el modo de control por falla:
 *
 * - false   -> la misma orden de marcha va a todas las salidas configuradas
 *              ("00"/"11" con dos salidas, "000"/"111" con tres).
 * - true    -> Q1 (linea principal) y Q3 (respaldo) se reparten la orden, asi
 *              que aparecen patrones mixtos ("110", "011", "101", ...).
 * - ausente -> se resuelve como false: el firmware antiguo no publica el campo
 *              y opera en modo todo-o-nada.
 *
 * Reglas. Unica fuente de verdad, replicada en
 * SW_SERVICE_MANAGER_BE/itgmanager/nanoox/generator_outputs.py:
 *
 *     encendido     = algun bit en 1                    (independiente del modo)
 *     inconsistente = modo false EXPLICITO y bits mezclados
 *
 * La inconsistencia NO se deriva del patron de bits en modo enclavado ni en
 * modo desconocido: la nube no puede enumerar de forma confiable los patrones
 * validos (p.ej. "101"), y declararlo produce alarmas criticas falsas.
 *
 * OJO con GEN_FAULT: en los datos reales GEN_FAULT/GEN_FAULT_LATCHED valen true
 * exactamente cuando el generador corre en la linea de respaldo ("011", con
 * output_fault_state activo) y false cuando corre en la principal ("110"). Es
 * la senal que dispara el traspaso a respaldo, NO una alarma del generador.
 *
 * El parser anterior troceaba el string en pares de 2 caracteres, asi que
 * "011" -> ["01","1"] se reportaba como inconsistencia. Ese era el bug.
 */

export const ALL_OR_NOTHING = "todo_o_nada";
export const INTERLOCKED = "enclavado";

const TEKFISH_MARKER = "falla_generador_tekfish";

/** Normaliza a true/false/null. null significa "el dato no permite decidir". */
export function asBool(value) {
  if (typeof value === "boolean") return value;
  if (value === 1) return true;
  if (value === 0) return false;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    if (text === "true" || text === "1") return true;
    if (text === "false" || text === "0") return false;
  }
  return null;
}

/** Devuelve el string de salidas si es utilizable, o null. */
export function normalizeOutputs(digitalOutputsState) {
  if (typeof digitalOutputsState !== "string") return null;
  const value = digitalOutputsState.trim();
  if (!value) return null;
  for (const char of value) {
    if (char !== "0" && char !== "1") return null;
  }
  return value;
}

/** Devuelve {modo, reportado}. Ausente -> {ALL_OR_NOTHING, false}. */
export function resolveControlMode(controlGeneratorBack) {
  const flag = asBool(controlGeneratorBack);
  if (flag === true) return { modo: INTERLOCKED, reportado: true };
  return { modo: ALL_OR_NOTHING, reportado: flag === false };
}

/** Los tekfish codifican `digital_outputs_state` de otra forma. */
export function isTekFish(snapshot) {
  return Boolean(snapshot) && typeof snapshot === "object" && TEKFISH_MARKER in snapshot;
}

/** Encendido = algun bit en 1. null si no hay dato utilizable. */
export function outputsAreOn(digitalOutputsState) {
  const outputs = normalizeOutputs(digitalOutputsState);
  if (outputs === null) return null;
  return outputs.includes("1");
}

/**
 * Solo se declara inconsistencia con control_generator_back = false explicito,
 * donde el contrato garantiza que todas las salidas reciben la misma orden.
 */
export function outputsAreInconsistent(digitalOutputsState, controlGeneratorBack) {
  if (asBool(controlGeneratorBack) !== false) return false;
  const outputs = normalizeOutputs(digitalOutputsState);
  if (outputs === null || outputs.length < 2) return false;
  return new Set(outputs).size > 1;
}

/** Encendido/apagado desde el snapshot completo. null si no se puede decidir. */
export function generatorIsOn(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;

  const outputs = normalizeOutputs(snapshot.digital_outputs_state);

  if (isTekFish(snapshot)) {
    // "10" = senal de encendido enviada, "01" = senal de apagado enviada.
    if (outputs === "10") return true;
    if (outputs === "01") return false;
    return null;
  }

  return outputsAreOn(outputs);
}

/** Orden de marcha segun la FSM (MODE/ON o REMOTE_ON). null si no la reporta. */
export function resolveRunOrder(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;
  if (snapshot.MODE) return asBool(snapshot.ON);
  return asBool(snapshot.REMOTE_ON);
}
